import React from 'react';
import styled from 'styled-components';
import { useLocalStorageState } from 'ahooks';
import {
  Layout,
  Row,
  Col,
  Flex,
  Typography,
  Card,
  Switch,
  Button,
  Divider,
  List,
  Empty,
} from 'antd';

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;

export const StyledWrapper = styled(Layout)`
  overflow: hidden;
  position: relative;
  height: max-content;
  min-height: 100%;
`;

export const StyledContent = styled(Content)`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  margin-bottom: 2rem;
`;

export const StyledFeatureCaption = styled(Flex)`
  padding: 2.4rem 0;
`;

export const BETA_TOGGLES = {
  notifications: {
    key: 'betaNotificationsEnabled',
  },
  onboardingProgressBanner: {
    key: 'betaOnboardingProgressBannerEnabled',
  },
};

export const SOFT_ROLLBACK_TOGGLES = {
  mainMenu: {
    key: 'softRollbackMainMenuEnabled',
  },
};

export function Beta() {
  const [showBetaNotifications, setShowBetaNotifications] =
    useLocalStorageState<boolean>(BETA_TOGGLES.notifications.key, {
      defaultValue: false,
      listenStorageChange: true,
    });

  const [
    showBetaOnboardingProgressBanner,
    setShowBetaOnboardingProgressBanner,
  ] = useLocalStorageState<boolean>(BETA_TOGGLES.onboardingProgressBanner.key, {
    defaultValue: false,
    listenStorageChange: true,
  });

  const [showSoftRollbackMainMenu, setShowSoftRollbackMainMenu] =
    useLocalStorageState<boolean>(SOFT_ROLLBACK_TOGGLES.mainMenu.key, {
      defaultValue: false,
      listenStorageChange: true,
    });

  const handleOnBetaNotificationsToggleChange = (checked: boolean) => {
    setShowBetaNotifications(checked);
    if (!checked) {
      setShowBetaOnboardingProgressBanner(false);
    }
  };

  const handleOnBetaSubscriptionUpgradeCardToggleChange = (
    checked: boolean
  ) => {};

  const handleOnBetaOnboardingProgressBannerToggleChange = (
    checked: boolean
  ) => {
    setShowBetaOnboardingProgressBanner(checked);
  };

  const handleOnSoftRollbackMainMenuToggleChange = (checked: boolean) => {
    setShowSoftRollbackMainMenu(checked);
  };

  return (
    <StyledWrapper>
      <StyledContent>
        <Row>
          <Col span={24}>
            <StyledFeatureCaption justify="flex-start" vertical>
              <Title level={3} style={{ fontWeight: 400, margin: 0 }}>
                Beta Features
              </Title>
              <Paragraph type="secondary">
                This page offers access to experimental features in beta
                testing, giving Beta Testing Program participants early access
                to new features before their official release.
              </Paragraph>
            </StyledFeatureCaption>
          </Col>
          <Col span={24}>
            <Card>
              <Row justify="space-between" align="middle" gutter={[16, 16]}>
                <Col md={16} sm={24} xs={24}>
                  <Title level={5} style={{ marginTop: 0 }}>
                    Beta Access Notifications
                  </Title>
                  <Paragraph type="secondary">
                    Enable this option to receive notifications whenever beta
                    features become available in specific parts of the
                    application. Once enabled, you’ll start seeing in-app alerts
                    when new beta features are ready for testing.
                  </Paragraph>
                </Col>
                <Col md={8} sm={24} xs={24}>
                  <Flex gap={16} align="end" vertical>
                    <Flex align="center" gap={16}>
                      <Text>Show Beta Notifications</Text>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        checked={showBetaNotifications}
                        onChange={handleOnBetaNotificationsToggleChange}
                      />
                    </Flex>
                  </Flex>
                </Col>
              </Row>
              <Row
                align="middle"
                gutter={[16, 16]}
                justify="space-between"
                style={{ marginTop: 16 }}
              >
                <Col md={16} sm={24} xs={24}>
                  <Title level={5} style={{ marginTop: 0 }}>
                    Share Your Feedback
                  </Title>
                  <Paragraph type="secondary">
                    Your feedback helps us improve beta features before their
                    official release. Let us know what you think!
                  </Paragraph>
                </Col>
                <Col md={8} sm={24} xs={24}>
                  <Flex gap={16} align="end" vertical>
                    <Button type="primary" disabled>
                      Share Feedback
                    </Button>
                  </Flex>
                </Col>
              </Row>
              {showBetaNotifications && (
                <>
                  <Row align="middle">
                    <Col span={24}>
                      <Divider style={{ margin: '16px 0' }} />
                    </Col>
                  </Row>
                  <Row
                    align="middle"
                    gutter={[16, 16]}
                    justify="space-between"
                    style={{ marginTop: 16 }}
                  >
                    <Col md={16} sm={24} xs={24}>
                      <Title level={5} style={{ marginTop: 0 }}>
                        Beta Features Available for Testing
                      </Title>
                      <Paragraph type="secondary">
                        Explore the latest beta features available for testing.
                        Help us improve these features by trying them out and
                        providing feedback.
                      </Paragraph>
                    </Col>
                    <Col md={24} sm={24} xs={24}>
                      <List bordered>
                        <List.Item hidden key="empty_list">
                          <Empty
                            description="No beta features available for testing."
                            style={{ width: '100%' }}
                          />
                        </List.Item>
                        <List.Item
                          key={BETA_TOGGLES.onboardingProgressBanner.key}
                          actions={[
                            <Flex align="center" gap={16}>
                              <Text>Try It</Text>
                              <Switch
                                checkedChildren="On"
                                unCheckedChildren="Off"
                                checked={showBetaOnboardingProgressBanner}
                                onChange={
                                  handleOnBetaOnboardingProgressBannerToggleChange
                                }
                              />
                            </Flex>,
                          ]}
                        >
                          <Flex vertical>
                            <Text strong>Onboarding Progress Banner</Text>
                            <Paragraph type="secondary">
                              This feature is designed to onboard merchants more
                              smoothly by guiding them through the essential
                              steps required to set up their account and start
                              running their online operations successfully.
                            </Paragraph>
                          </Flex>
                        </List.Item>
                      </List>
                    </Col>
                  </Row>
                </>
              )}
              <>
                <Row align="middle">
                  <Col span={24}>
                    <Divider style={{ margin: '16px 0' }} />
                  </Col>
                </Row>
                <Row
                  align="middle"
                  gutter={[16, 16]}
                  justify="space-between"
                  style={{ marginTop: 16 }}
                >
                  <Col md={16} sm={24} xs={24}>
                    <Title level={5} style={{ marginTop: 0 }}>
                      Soft Rollback (Pre-Report Verification)
                    </Title>
                    <Paragraph type="secondary">
                      Before reporting an issue, use Soft Rollback to verify
                      whether the behavior is truly a bug or part of the new
                      rollout. This feature allows you to temporarily compare
                      the new version with the previous stable version directly
                      in your local browser. By validating the behavior first,
                      you help reduce false bug reports and give the development
                      team clearer, more actionable feedback.
                    </Paragraph>
                  </Col>
                  <Col md={24} sm={24} xs={24}>
                    <List bordered>
                      <List.Item key="empty_list" hidden>
                        <Empty
                          description="No features available to try soft rollback."
                          style={{ width: '100%' }}
                        />
                      </List.Item>
                      <List.Item
                        key="soft_rollback_main_menu"
                        actions={[
                          <Flex align="center" gap={16}>
                            <Text>Try It</Text>
                            <Switch
                              checkedChildren="On"
                              unCheckedChildren="Off"
                              checked={showSoftRollbackMainMenu}
                              onChange={
                                handleOnSoftRollbackMainMenuToggleChange
                              }
                            />
                          </Flex>,
                        ]}
                      >
                        <Flex vertical>
                          <Text strong>Main Menu & Top Header</Text>
                          <Paragraph type="secondary">
                            Not sure if it’s a bug? Use Soft Rollback to
                            temporarily switch back to the previous menu version
                            and confirm whether the issue is specific to the new
                            rollout.
                          </Paragraph>
                        </Flex>
                      </List.Item>
                    </List>
                  </Col>
                </Row>
              </>
            </Card>
          </Col>
        </Row>
      </StyledContent>
    </StyledWrapper>
  );
}
